import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Calendar, Pencil, X } from "lucide-react";
import { DataActionsContext } from "@/App";

/* ── Carrier tracking ───────────────────────────────────────────────────────
 * Map a (free-text) carrier name to its official tracking URL. Matching is
 * fuzzy + case-insensitive because dispatch staff type the carrier by hand
 * ("DHL", "DHL Logistics", "dhl express" all resolve to DHL). An UNKNOWN
 * carrier returns "" so the UI shows no Track button — we never fire a wrong
 * link. URLs are hardcoded (trusted), only the tracking id is interpolated.
 *
 * ⚠️ URL patterns can change — verify each carrier's link opens correctly
 * before relying on it for that carrier.
 */
const CARRIER_TRACKING = [
  { match: /\bdhl\b/i,                         url: (id) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encodeURIComponent(id)}&submit=1` },
  { match: /\bfedex\b/i,                       url: (id) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(id)}` },
  { match: /\bups\b/i,                         url: (id) => `https://www.ups.com/track?tracknum=${encodeURIComponent(id)}` },
  { match: /\bblue\s?dart\b/i,                 url: (id) => `https://www.bluedart.com/web/guest/trackdartresultthird?trackFor=0&trackNo=${encodeURIComponent(id)}` },
  { match: /\b(india\s?post|speed\s?post)\b/i, url: (id) => `https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx?logicalname=${encodeURIComponent(id)}` },
  { match: /\bdelhivery\b/i,                    url: (id) => `https://www.delhivery.com/track/package/${encodeURIComponent(id)}` },
  { match: /\baramex\b/i,                       url: (id) => `https://www.aramex.com/us/en/track/results?ShipmentNumber=${encodeURIComponent(id)}` },
  { match: /\bdtdc\b/i,                         url: (id) => `https://www.dtdc.in/tracking/tracking_results.asp?strCnno=${encodeURIComponent(id)}` },
];

// Returns a tracking URL for the carrier, or "" if the carrier is unknown or
// the id is missing.
export function carrierTrackingUrl(carrierName, trackingId) {
  const id = (trackingId || "").trim();
  const carrier = (carrierName || "").trim();
  if (!id || !carrier) return "";
  const hit = CARRIER_TRACKING.find((c) => c.match.test(carrier));
  return hit ? hit.url(id) : "";
}

/* Open an external tracking URL safely, AND survive RockLink running as an
 * installed Chrome web app (standalone display mode). In standalone PWAs a
 * plain window.open(_blank) is sometimes swallowed (no tab strip to land in) —
 * so if the call returns null/undefined we fall back to a sized popup window,
 * which standalone PWAs do honor. noopener+noreferrer on both paths. */
export function openTrackingUrl(url) {
  if (!url) return;
  // Open via a synthetic anchor click (the pattern used elsewhere in the app):
  // target=_blank + rel=noopener,noreferrer. This opens exactly ONE new tab and
  // never touches the current one.
  //
  // NOTE: do NOT pass "noopener" in a window.open feature string — by spec that
  // makes window.open return null even on success, which previously tricked a
  // fallback into firing a SECOND open (and a same-tab navigation). The anchor
  // approach avoids that entirely.
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  } catch (_) { /* fall through */ }
  // Fallback only if the anchor path threw (very old/embedded webviews): open
  // with a handle and sever opener manually. Single call — no double-open.
  let win = null;
  try { win = window.open(url, "_blank"); } catch (_) { win = null; }
  if (win) { try { win.opener = null; } catch (_) {} }
}

export const PROCUREMENT_STATUSES = ["Pending", "Ordered", "Partially Received", "Complete"];
export const MANUFACTURING_STATUSES = ["Queued", "Processing", "Third Party", "Machining Complete"];
export const QC_STATUSES = ["Pending Inspection", "Under Inspection", "Failed", "Rework Required", "Passed"];
export const DISPATCH_STATUSES = ["Pending", "Pickup Awaited", "Negotiating Rates", "Shipped"];

export const PROC_TEAM_COLORS = {
  Material: { color: "#a21caf", bg: "rgba(162,28,175,0.10)", border: "rgba(162,28,175,0.25)" },
  Tooling:  { color: "#0891b2", bg: "rgba(8,145,178,0.10)", border: "rgba(8,145,178,0.25)" },
  Gauging:  { color: "#65a30d", bg: "rgba(101,163,13,0.10)", border: "rgba(101,163,13,0.25)" },
};

export function parseDate(str) {
  if (!str) return null;
  if (str.includes("T")) return new Date(str);
  if (str.length >= 10 && str[4] === "-") return new Date(str + "T00:00:00");
  const parts = str.split("/");
  if (parts.length === 3) {
    const [d, m, y] = parts;
    const fullYear = y.length === 2 ? `20${y}` : y;
    const candidate = new Date(`${fullYear}-${m.padStart(2,"0")}-${d.padStart(2,"0")}T00:00:00`);
    if (!isNaN(candidate)) return candidate;
  }
  return null;
}

export function formatDateDMY(str) {
  if (!str) return "—";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const d = parseDate(str);
  if (!d || isNaN(d)) return str;
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

/** Compact ETA label shown beneath an "Ordered" procurement subteam.
 *  Empty / invalid value → "N/A". */
export function formatEtaShort(iso) {
  if (!iso) return "N/A";
  const d = parseDate(iso);
  if (!d || isNaN(d)) return "N/A";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

export function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

export function getStageAgeSecs(item) {
  const sa = item?.stage_ages?.[item.stage];
  if (!sa) return 0;
  let total = sa.total_seconds || 0;
  if (sa.entered_at) {
    try { total += (Date.now() - new Date(sa.entered_at).getTime()) / 1000; } catch (_) {}
  }
  return total;
}

export function formatStageAge(secs) {
  const days = Math.floor(secs / 86400);
  const hrs  = Math.floor((secs % 86400) / 3600);
  if (days === 0 && hrs === 0) return "< 1h";
  if (days === 0) return `${hrs}h`;
  // Hours matter early (day 1) but become noise once an item has sat for days —
  // a stuck row just needs "how long", not "how long to the hour". Drop the
  // hours from 2 days on: "53d 23h" → "53d".
  if (days >= 2) return `${days}d`;
  return `${days}d ${hrs}h`;
}

export function ageColor(secs) {
  const d = secs / 86400;
  if (d >= 10) return "#ef4444";
  if (d >= 5)  return "#f59e0b";
  return null;
}

export function ProcStatusBadge({ status, label }) {
  // `status` is the stored KEY (drives colour); `label` is the per-division
  // display text (Phase 1b — e.g. drilling shows "Done" for key "Complete").
  const map = {
    "Pending":            { cls: "bg-zinc-500/10 text-zinc-500 rl-edge-slate" },
    "Ordered":            { cls: "bg-blue-500/10 text-blue-600 rl-edge-blue" },
    "Partially Received": { cls: "bg-sky-500/10 text-sky-600 rl-edge-blue" },
    "Received":           { cls: "bg-teal-500/10 text-teal-600 rl-edge-teal" },
    "In Progress":        { cls: "bg-amber-500/10 text-amber-600 rl-edge-amber" },
    "Complete":           { cls: "bg-green-500/10 text-green-600 rl-edge-green" },
  };
  const s = map[status] || map["Pending"];
  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded rl-glass-hairline ${s.cls}`}>{label || status}</span>
  );
}

function toISODate(str) {
  const d = parseDate(str);
  if (!d || isNaN(d)) return "";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export function DispatchDueCell({ dueDateStr, isDispatchDue, shippedAt, compact = false }) {
  const dueDate = compact ? "text-[11px]" : "text-[13px]";
  const dueChip = compact ? "text-[10px]" : "text-[11px]";
  if (!dueDateStr) return <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>;
  const today = new Date(); today.setHours(0,0,0,0);
  const due = parseDate(dueDateStr);
  if (!due || isNaN(due)) return <span className={`${dueDate} font-mono-data`}>{dueDateStr}</span>;
  due.setHours(0,0,0,0);
  const dateLabel = formatDateDMY(dueDateStr);
  const isoDate = toISODate(dueDateStr);

  // If item has been shipped, show early/late instead of overdue/remaining
  if (shippedAt) {
    const shipped = parseDate(shippedAt);
    if (shipped && !isNaN(shipped)) {
      shipped.setHours(0,0,0,0);
      const diff = Math.round((shipped - due) / 86400000);
      return (
        <div className="flex flex-col items-center gap-0.5">
          <time dateTime={isoDate} className={`block ${dueDate} font-mono-data font-semibold`}>{dateLabel}</time>
          {diff > 0 ? (
            <span className={`inline-flex items-center ${dueChip} font-mono-data font-bold px-1.5 py-0 rounded-sm bg-red-500/15 text-red-600 dark:text-red-400`}>
              {diff}d late
            </span>
          ) : diff < 0 ? (
            <span className={`inline-flex items-center ${dueChip} font-mono-data font-bold px-1.5 py-0 rounded-sm bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}>
              {Math.abs(diff)}d early
            </span>
          ) : (
            <span className={`inline-flex items-center ${dueChip} font-mono-data font-bold px-1.5 py-0 rounded-sm bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}>
              On time
            </span>
          )}
        </div>
      );
    }
  }

  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const lateDays = Math.abs(diffDays);
    const severe = lateDays > 60;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <time dateTime={isoDate} className={`block ${dueDate} font-mono-data font-semibold`}>{dateLabel}</time>
        <span className={`inline-flex items-center gap-1 ${dueChip} font-mono-data ${severe ? "font-semibold text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}>
          <span className={`w-[5px] h-[5px] rounded-full ${severe ? "bg-red-500" : "bg-amber-500"}`} aria-hidden="true" />
          {lateDays}d overdue
        </span>
      </div>
    );
  }
  const dueSoon = diffDays <= 7;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <time dateTime={isoDate} className={`block ${dueDate} font-mono-data font-semibold`}>{dateLabel}</time>
      {dueSoon ? (
        <span className={`inline-flex items-center gap-1 ${dueChip} font-mono-data text-amber-600 dark:text-amber-400`}>
          <span className="w-[5px] h-[5px] rounded-full bg-amber-500" aria-hidden="true" />
          {diffDays}d remaining
        </span>
      ) : (
        <div className={`${dueChip} text-[hsl(var(--muted-foreground))]`}>{diffDays}d remaining</div>
      )}
    </div>
  );
}

/**
 * Completed stage — show just a plain date. If the shipment completed AFTER
 * the original due date, color it red with a "late" tag so we can spot
 * historical misses at a glance.
 *
 * Pass both the due date and the actual completion date so we can compare.
 * If completionDateStr is missing, we fall back to showing only the due date.
 */
export function CompletedDueCell({ dueDateStr, completionDateStr }) {
  if (!dueDateStr) return <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>;
  const due = parseDate(dueDateStr);
  if (!due || isNaN(due)) return <span className="text-xs font-mono-data">{dueDateStr}</span>;
  due.setHours(0,0,0,0);
  const completion = parseDate(completionDateStr);
  // No ship date on file? Show the due date plain — without speculating
  // about "Nd late". Lateness is only knowable when we have an actual
  // shipped_at to compare against the due date.
  let lateDays = 0;
  let hasCompletion = false;
  if (completion && !isNaN(completion)) {
    completion.setHours(0,0,0,0);
    lateDays = Math.floor((completion - due) / 86400000);
    hasCompletion = true;
  }
  const isLate  = hasCompletion && lateDays > 0;
  const isEarly = hasCompletion && lateDays < 0;
  const dateLabel = formatDateDMY(dueDateStr);
  const isoDate = toISODate(dueDateStr);
  return (
    <div className="flex flex-col items-center gap-0.5">
      <time
        dateTime={isoDate}
        className={`block text-xs font-mono-data font-semibold ${isLate ? "text-red-500" : ""}`}
      >
        {dateLabel}
      </time>
      {isLate && (
        <span className="inline-flex items-center text-[10px] font-mono-data font-bold px-1.5 py-0 rounded-sm bg-red-500/15 text-red-600 dark:text-red-400">
          {lateDays}d late
        </span>
      )}
      {isEarly && (
        <span className="inline-flex items-center text-[10px] font-mono-data font-bold px-1.5 py-0 rounded-sm bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          {Math.abs(lateDays)}d early
        </span>
      )}
    </div>
  );
}

/**
 * Completed stage — date the item was shipped.
 * Source: item.shipped_at — stamped by the backend the first time the
 * Dispatch team flips dispatch_status to "Shipped". Legacy items that
 * completed before this stamp existed render as a calm "Unknown" placeholder
 * with an inline "set" action (only for users with edit rights).
 */
export function CompletedOnCell({ item, canEdit = false, emptyLabel = "Unknown" }) {
  const { editItem } = useContext(DataActionsContext) || {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const iso = item?.shipped_at;
  const d = iso ? new Date(iso) : null;
  const valid = d && !isNaN(d);

  const save = async (value) => {
    if (!editItem) return;
    const v = (value ?? draft ?? "").trim();
    if (!v) { setEditing(false); return; }
    setSaving(true);
    try {
      await editItem(item.id, { shipped_at: v });
      setEditing(false);
      setDraft("");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`shipped-on-edit-${item.id}`}>
        <input
          type="date"
          autoFocus
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (draft) save(draft); else setEditing(false); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setEditing(false); setDraft(""); }
          }}
          className="h-6 px-1.5 text-xs font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-emerald-500"
          max={new Date().toISOString().slice(0, 10)}
          data-testid={`shipped-on-input-${item.id}`}
        />
      </div>
    );
  }

  if (!valid) {
    if (canEdit && editItem) {
      // Whole "Unknown ✎" is one quiet edit target — no underlined link, no
      // separate SET word. Pencil reveals on row hover like the filled state.
      return (
        <button
          type="button"
          onClick={() => { setDraft(new Date().toISOString().slice(0, 10)); setEditing(true); }}
          className="group/setdate inline-flex items-center rounded px-0.5 text-xs text-[hsl(var(--muted-foreground))]/55 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 transition-colors"
          title="Set shipped date"
          data-testid={`shipped-on-set-${item.id}`}
        >
          <span className="italic">Unknown</span>
          <span className="inline-flex items-center max-w-0 overflow-hidden opacity-0 group-hover/setdate:max-w-[16px] group-hover/setdate:opacity-100 group-hover/setdate:ml-1 transition-all duration-150" aria-hidden="true">
            <Pencil className="w-2.5 h-2.5 shrink-0" />
          </span>
        </button>
      );
    }
    return (
      <span className="text-xs text-[hsl(var(--muted-foreground))]/55 italic" data-testid={`shipped-on-unknown-${item.id}`}>
        {emptyLabel}
      </span>
    );
  }

  if (valid) {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const label = `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
    const iso10 = d.toISOString().slice(0, 10);
    return (
      <div className="group/ship inline-flex items-center gap-1">
        {/* The date itself is the edit target (hover pencil) — same vocabulary
            as the TR/INV pills. Clear is one quiet × icon, not a word. No
            extra horizontal text chrome. */}
        {canEdit && editItem ? (
          <button
            type="button"
            onClick={() => { setDraft(iso10); setEditing(true); }}
            className="group/shipdate inline-flex items-center rounded px-0.5 hover:bg-[hsl(var(--muted))]/40 transition-colors cursor-pointer"
            title={`Edit shipped date${item.shipped_by ? ` · shipped by ${item.shipped_by}` : ""}`}
            data-testid={`shipped-on-edit-btn-${item.id}`}
          >
            <time dateTime={iso10} className="block text-xs font-mono-data font-semibold text-emerald-600 dark:text-emerald-400">
              {label}
            </time>
            {/* Pencil collapsed at rest; slides out as this target widens when
                hovered locally (not on whole-row hover). */}
            <span className="inline-flex items-center max-w-0 overflow-hidden opacity-0 group-hover/shipdate:max-w-[16px] group-hover/shipdate:opacity-100 group-hover/shipdate:ml-1 transition-all duration-150" aria-hidden="true">
              <Pencil className="w-2.5 h-2.5 shrink-0 text-[hsl(var(--muted-foreground))]/60" />
            </span>
          </button>
        ) : (
          <time
            dateTime={iso10}
            className="block text-xs font-mono-data font-semibold text-emerald-600 dark:text-emerald-400"
            title={item.shipped_by ? `Shipped by ${item.shipped_by}` : undefined}
          >
            {label}
          </time>
        )}
        {canEdit && editItem && (
          <button
            type="button"
            onClick={async () => {
              setSaving(true);
              try { await editItem(item.id, { shipped_at: null }); }
              finally { setSaving(false); }
            }}
            disabled={saving}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[hsl(var(--muted-foreground))]/50 opacity-0 group-hover/ship:opacity-100 hover:text-rose-500 hover:bg-rose-500/10 transition-all disabled:opacity-20"
            title="Clear shipped date"
            aria-label="Clear shipped date"
            data-testid={`shipped-on-clear-btn-${item.id}`}
          >
            <X className="w-2.5 h-2.5" strokeWidth={2.5} />
          </button>
        )}
      </div>
    );
  }
}

function TruncatedValue({ value, className = "" }) {
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [value]);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  }, []);

  const show = () => {
    if (!truncated) return;
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    place();
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  return (
    <>
      <span
        ref={ref}
        className={`truncate ${className}`}
        title={truncated ? undefined : value}
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onFocus={show}
        onBlur={scheduleClose}
        tabIndex={truncated ? 0 : undefined}
      >
        {value}
      </span>
      {open && createPortal(
        <div
          role="tooltip"
          onMouseEnter={() => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } }}
          onMouseLeave={scheduleClose}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(value).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
                toast.success(`Copied ${value} to clipboard`);
              }).catch(() => {});
            }
          }}
          title="Click to copy"
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999, cursor: "pointer" }}
          className="max-w-[320px] px-2 py-1 text-[11px] font-mono-data leading-snug rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover,var(--card)))] text-[hsl(var(--foreground))] shadow-md select-text break-all"
        >
          {copied ? "Copied" : value}
        </div>,
        document.body
      )}
    </>
  );
}

/* Ticket/Invoice ledger ("TR" data fields = Ticket Raised; the rename is
 * label-only — tr_number etc. stay untouched in the data model).
 * Quiet-ledger design: neutral grey label spine, mono value pills, muted
 * "· date" trail. Empty fields rest as a faint dash; "+ add" affordances
 * appear on row hover (desktop passes hoverReveal + the row's group/row
 * scope) and stay PERSISTENT in the actionable pre-ship states (Pickup
 * Awaited / Negotiating Rates) — the nudge lives exactly where the work is.
 * Mobile has no hover, so affordances are always visible there. */
export function TrInvoiceCell({ item, canEdit = false, hoverReveal = false, isCompact = false, showTr = true }) {
  const { editItem } = useContext(DataActionsContext) || {};
  // editing holds a composite key: "tr" | "invoice" | "tr_date" | "invoice_date" | null
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Pending = dispatch hasn't started, so Ticket/Invoice affordances are noise —
  // hide the whole ledger. BUT if any value already exists (status reverted,
  // or data entered early), keep showing it so data never disappears.
  const dispatchStatus = item?.dispatch_status || "Pending";
  const hasAnyTrInvoice = !!(item?.tr_number || item?.invoice_number || item?.tr_date || item?.invoice_date);
  const hiddenWhilePending = dispatchStatus === "Pending" && !hasAnyTrInvoice;

  // Adaptive label column: once the cell holds ANY value, BOTH labels compress
  // (TR / INV) and the label gutter narrows so the data pulls left. Fully empty
  // cells keep the wide self-explanatory words (Ticket / Invoice).
  const cellFilled = !!(item?.tr_number || item?.invoice_number);

  // Pre-ship states are where raising a ticket / cutting an invoice is the
  // live next action → affordances always visible. Elsewhere, hover-only.
  const actionable = dispatchStatus === "Pickup Awaited" || dispatchStatus === "Negotiating Rates";
  const affordanceAlways = !hoverReveal || actionable;

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${String(d.getDate()).padStart(2,"0")}-${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
  };
  const today = () => new Date().toISOString().slice(0, 10);

  // label = teaching name shown while empty; shortLabel = compact ledger code
  // once a value exists (filled entries darken + compress, empties recede).
  const cols = [
    { numKey: "tr",      numField: "tr_number",      dateKey: "tr_date",      dateField: "tr_date",      label: "Ticket",  shortLabel: "TR"  },
    { numKey: "invoice", numField: "invoice_number", dateKey: "invoice_date", dateField: "invoice_date", label: "Invoice", shortLabel: "INV" },
  ].filter(c => showTr || c.numKey !== "tr");   // Phase 1b: drilling hides the Ticket row, keeps Invoice

  const begin = (key, currentVal) => { setDraft(currentVal || ""); setEditing(key); };
  const cancel = () => { setEditing(null); setDraft(""); };
  const save = async (field, dataType) => {
    if (!editItem) return cancel();
    let v = (draft ?? "").trim();
    if (v === (item?.[field] || "")) return cancel();
    setSaving(true);
    try { await editItem(item.id, { [field]: v }); cancel(); }
    finally { setSaving(false); }
  };

  // Compact density: shrink the whole ledger the way the row's status controls
  // do — tighter row gap, smaller value/label text, shorter slot height — so
  // the Dispatch cell stops towering over its (now h-6) status dropdown.
  const slotH   = isCompact ? "h-[18px]" : "h-5";
  const valTxt  = isCompact ? "text-[10px]" : "text-[11px]";
  const dateTxt = isCompact ? "text-[9px]" : "text-[10px]";
  const lblTxt  = isCompact ? "text-[8.5px]" : "text-[9.5px]";
  const rowGap  = isCompact ? "gap-y-[1px]" : "gap-y-[3px]";
  const topPad  = isCompact ? "pt-0.5" : "pt-1";
  const inputCls = `${slotH} w-24 px-1 ${valTxt} font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-amber-500`;

  if (hiddenWhilePending) return null;

  // Ledger layout: a constant 2-row grid (TR / Invoice) so every state —
  // empty, mixed, full — aligns identically down the column. Label spine on
  // the left, mono value pill (or dashed "+ add"), date trailing with a
  // small calendar glyph (dashed "+ date" affordance until set).
  return (
    <div className={`grid ${cellFilled ? "grid-cols-[28px_minmax(0,1fr)]" : "grid-cols-[48px_minmax(0,1fr)]"} gap-x-2 ${rowGap} items-center ${topPad} pl-1`} data-testid={`tr-invoice-${item.id}`}>
      {cols.map((c) => {
        const num = item?.[c.numField];
        const dateVal = item?.[c.dateField];
        return (
          <React.Fragment key={c.numKey}>
            <span className={`${lblTxt} font-semibold uppercase tracking-[0.08em] text-right leading-none ${num ? "text-[hsl(var(--foreground))]/75" : "text-[hsl(var(--muted-foreground))]/65"}`}>
              {cellFilled ? c.shortLabel : c.label}
            </span>
            <span className="inline-flex items-center gap-1.5 min-w-0 justify-self-start">
              {editing === c.numKey ? (
                <input
                  autoFocus disabled={saving} value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => save(c.numField, "text")}
                  onKeyDown={(e) => { if (e.key === "Enter") save(c.numField, "text"); if (e.key === "Escape") cancel(); }}
                  placeholder={c.label} className={inputCls}
                  data-testid={`tr-invoice-input-${c.numKey}-${item.id}`}
                />
              ) : num ? (
                <button type="button" onClick={canEdit ? () => begin(c.numKey, num) : undefined}
                  className={`group/trpill inline-flex items-center ${valTxt} font-mono-data font-medium px-2 py-px rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill min-w-0 ${canEdit ? "hover:bg-[hsl(var(--muted))]/40 cursor-pointer" : "cursor-default"} transition-colors`}
                  title={canEdit ? `Edit ${c.label.toLowerCase()} number` : undefined}
                  data-testid={`tr-invoice-filled-${c.numKey}-${item.id}`}>
                  <TruncatedValue value={num} className="text-[hsl(var(--foreground))]/85 max-w-[104px]" />
                  {canEdit && (
                    /* Pencil stays fully collapsed at rest (max-w-0, no gap) so
                       the pill hugs the number; on hovering THIS pill the slot
                       expands and the pill grows to reveal the pencil sliding
                       out — local to the object, not the whole row. */
                    <span className="inline-flex items-center max-w-0 overflow-hidden opacity-0 group-hover/trpill:max-w-[16px] group-hover/trpill:opacity-100 group-hover/trpill:ml-1 transition-all duration-150" aria-hidden="true">
                      <Pencil className="w-2.5 h-2.5 shrink-0 text-[hsl(var(--muted-foreground))]/60" />
                    </span>
                  )}
                </button>
              ) : canEdit ? (
                /* Constant-height slot: the "+ add" pill is ALWAYS in the layout
                 * (so row height never changes) and only fades in on row hover;
                 * the resting dash is absolutely positioned so it adds no size. */
                <span className={`relative inline-flex items-center ${slotH}`}>
                  {!affordanceAlways && (
                    <span className={`absolute left-0 ${valTxt} text-[hsl(var(--muted-foreground))]/35 group-hover/row:opacity-0 transition-opacity`} aria-hidden="true">—</span>
                  )}
                  <button type="button" onClick={() => begin(c.numKey, "")}
                    className={`inline-flex items-center ${slotH} leading-none ${valTxt} text-[hsl(var(--muted-foreground))]/70 border border-dashed border-[hsl(var(--border))] rounded-full px-2 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 transition-[opacity,colors] ${affordanceAlways ? "" : "opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto"}`}
                    data-testid={`tr-invoice-empty-${c.numKey}-${item.id}`}>
                    + add
                  </button>
                </span>
              ) : (
                <span className={`inline-flex items-center ${slotH} ${valTxt} text-[hsl(var(--muted-foreground))]/35`}>—</span>
              )}

              {/* date — only meaningful once the number exists */}
              {num ? (
                editing === c.dateKey ? (
                  <input
                    type="date" autoFocus disabled={saving} value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => save(c.dateField, "date")}
                    onKeyDown={(e) => { if (e.key === "Enter") save(c.dateField, "date"); if (e.key === "Escape") cancel(); }}
                    className={`${slotH} px-1 ${dateTxt} font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] focus:outline-none focus:ring-1 focus:ring-amber-500`}
                    max={today()}
                    data-testid={`tr-invoice-date-input-${c.numKey}-${item.id}`}
                  />
                ) : dateVal ? (
                  <span className="group/date inline-flex items-center gap-0.5 shrink-0">
                    <button type="button" onClick={canEdit ? () => begin(c.dateKey, dateVal) : undefined}
                      className={`inline-flex items-center gap-1 ${dateTxt} font-mono-data text-[hsl(var(--muted-foreground))]/80 rounded px-0.5 ${canEdit ? "hover:text-[hsl(var(--foreground))] cursor-pointer" : "cursor-default"} transition-colors`}
                      data-testid={`tr-invoice-date-filled-${c.numKey}-${item.id}`}>
                      {fmtDate(dateVal)}
                    </button>
                    {canEdit && (
                      <button type="button"
                        onClick={() => editItem && editItem(item.id, { [c.dateField]: "" })}
                        className="text-[10px] leading-none text-[hsl(var(--muted-foreground))]/40 hover:text-[hsl(var(--destructive))] opacity-0 group-hover/date:opacity-100 transition-opacity"
                        aria-label="Clear date"
                        data-testid={`tr-invoice-date-clear-${c.numKey}-${item.id}`}>
                        ×
                      </button>
                    )}
                  </span>
                ) : canEdit ? (
                  /* Same constant-height rule: always in flow, fades on hover. */
                  <button type="button" onClick={() => begin(c.dateKey, today())}
                    className={`inline-flex items-center gap-1 ${slotH} ${dateTxt} font-mono-data text-[hsl(var(--muted-foreground))]/60 border border-dashed border-[hsl(var(--border))] rounded-full px-1.5 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 transition-[opacity,colors] shrink-0 ${affordanceAlways ? "" : "opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto"}`}
                    data-testid={`tr-invoice-date-empty-${c.numKey}-${item.id}`}>
                    <Calendar className="w-2.5 h-2.5 shrink-0 opacity-60" aria-hidden="true" />
                    date
                  </button>
                ) : null
              ) : null}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
